import React, { useState, useEffect } from "react";
import { ImageOff } from "lucide-react";
import { cn } from "@/utils";

import { getOptimizedImageUrl } from "@/utils";



export default function OptimizedImage({
    src,
    alt,
    width,
    height,
    className,
    quality = 80,
    ...props
}) {
    const [isLoading, setIsLoading] = useState(true);
    const [imgSrc, setImgSrc] = useState(src);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (!src) return;

        // Use the unified getOptimizedImageUrl helper
        // This ensures Swipe preloader and this component generate identical URLs
        // Note: getOptimizedImageUrl handles Retina scaling internally
        const optimizedUrl = getOptimizedImageUrl(src, width, quality);

        // If height is provided and it's a Shopify URL, we might want to support it?
        // Current utils/getOptimizedImageUrl signature only takes (url, width, quality).
        // If we want to support height, we should update utils or assume width-based is sufficient for now.
        // The implementation plan focused on Preloading which typically uses width.
        // Let's rely on width-based optimization for consistency with preloader.

        setImgSrc(optimizedUrl);
        setHasError(false);
        setIsLoading(true);

    }, [src, width, height, quality]);

    return (
        <div
            className={cn("relative overflow-hidden bg-gray-100", className)}
        >
            {isLoading && !hasError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
                    {/* Optional: Add a subtle logo or icon here if desired */}
                </div>
            )}

            {hasError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-200 text-gray-400">
                    <ImageOff className="w-8 h-8" />
                </div>
            )}

            <img
                src={imgSrc}
                alt={alt || ""}
                className={cn(
                    "w-full h-full object-cover transition-opacity duration-300",
                    isLoading ? "opacity-0" : "opacity-100"
                )}
                loading="lazy"
                decoding="async"
                onLoad={() => setIsLoading(false)}
                onError={() => {
                    // Fallback to original source if proxy fails
                    if (imgSrc !== src && !src.startsWith('data:') && !src.startsWith('/')) {
                        // console.warn("OptimizedImage: Proxy failed, falling back to original source.");
                        setImgSrc(src);
                        setIsLoading(true); // Restart loading for fallback
                        setHasError(false);
                    } else {
                        setIsLoading(false);
                        setHasError(true);
                    }
                }}
                {...props}
            />
        </div>
    );
}
